import { useRouter } from "next/router"
import { type PropsWithChildren } from "react"
import { Container } from "@octopus-energy/coral/atoms"
import { queryOptions, useQuery } from "@tanstack/react-query"
import { type BlueprintGraphQLClient } from "@krakentech/blueprint-auth"
import CustomHead from "@/components/atoms/CustomHead"
import { ErrorBoundary } from "@/components/molecules/ErrorBoundary"
import { isPaymentInstructionValid } from "@/entities/paymentInstruction"
import { AppLayout } from "@/features/navigation"
import { isElmyAccount } from "@/features/partnership/elmy"
import { graphql, type VariablesOf } from "@/graphql"
import { useGraphQLClient } from "@/lib/blueprint-auth/client"
import { AccountNumberSchema as AccountNumberParamsSchema } from "@/schemas/queryParamsSchema"
import { getPaginatedNodes } from "@/utils/getPaginatedNodes"
import { AgreementWithInvalidPaymentInstructionBanner } from "./AgreementWithInvalidPaymentInstructionBanner"

const DashboardLayoutContainerQuery = graphql(
    `
        query DashboardLayoutContainer($accountNumber: String!, $activeAt: DateTime!) {
            account(accountNumber: $accountNumber) {
                ...isElmyAccount_Account
                agreements(activeAt: $activeAt, last: 10) {
                    edges {
                        node {
                            isActive
                            currentPaymentInstruction {
                                ...isPaymentInstructionValid_PaymentInstruction
                            }
                            ...AgreementWithInvalidPaymentInstructionBanner_Agreement
                        }
                    }
                }
            }
        }
    `,
    [
        AgreementWithInvalidPaymentInstructionBanner.fragments.agreement,
        isPaymentInstructionValid.fragments.paymentInstruction,
        isElmyAccount.fragments.account,
    ]
)

type DashboardLayoutContainerQueryVariables = Pick<
    VariablesOf<typeof DashboardLayoutContainerQuery>,
    "accountNumber"
>

function getDashboardLayoutContainerQueryOptions(
    client: BlueprintGraphQLClient,
    variables: DashboardLayoutContainerQueryVariables
) {
    return queryOptions({
        queryKey: ["accounts", variables.accountNumber, "layout"],
        queryFn: async () => {
            const { account } = await client.request(DashboardLayoutContainerQuery, {
                ...variables,
                activeAt: new Date().toISOString(),
            })

            // TODO: remove once Elmy migration is complete
            if (account && isElmyAccount(account)) {
                return null
            }

            const agreements = getPaginatedNodes(account?.agreements)
            const activeAgreements = agreements.filter((agreement) => agreement.isActive)

            const agreementWithInvalidPaymentInstruction = activeAgreements.find(
                (agreement) =>
                    !agreement.currentPaymentInstruction ||
                    !isPaymentInstructionValid(agreement.currentPaymentInstruction)
            )

            return agreementWithInvalidPaymentInstruction || null
        },
    })
}

const AccountNumberSchema = AccountNumberParamsSchema.shape.accountNumber.optional()

export function DashboardLayoutContainer({ children }: PropsWithChildren) {
    const router = useRouter()
    const graphQLClient = useGraphQLClient()

    const accountNumber = AccountNumberSchema.parse(router.query.accountNumber)

    const queryOptions = getDashboardLayoutContainerQueryOptions(graphQLClient, {
        accountNumber: accountNumber || "",
    })

    const queryResult = useQuery({
        ...queryOptions,
        enabled: !!router.query.accountNumber,
    })

    return (
        <AppLayout>
            {queryResult.data && accountNumber && (
                <AgreementWithInvalidPaymentInstructionBanner
                    agreement={queryResult.data}
                    accountNumber={accountNumber}
                />
            )}

            <CustomHead />

            <Container
                maxWidthPreset="lg"
                marginX="auto"
                marginTop={{ base: "md", md: "xxl", xl: "xxxl" }}
                paddingX={{ md: "mdLg" }}
            >
                <ErrorBoundary>{children}</ErrorBoundary>
            </Container>
        </AppLayout>
    )
}

DashboardLayoutContainer.messages = [
    ...AppLayout.messages,
    ...ErrorBoundary.messages,
    ...AgreementWithInvalidPaymentInstructionBanner.messages,
] as const

DashboardLayoutContainer.queryOptions = {
    account: getDashboardLayoutContainerQueryOptions,
}
